import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import TaskSkeleton from "./TaskSkeleton";
import moment from "moment";

function downloadCSV(rows, filename) {
  const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const MONTHS = [
    { value: 1, label: "Jan" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
    { value: 4, label: "Apr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
    { value: 7, label: "Jul" }, { value: 8, label: "Aug" }, { value: 9, label: "Sep" },
    { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dec" },
];

function getYears() {
    const current = new Date().getFullYear();
    return [current, current - 1, current - 2, current - 3];
}

function getMonthRange(year, month) {
    const start = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const end = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    return { start, end };
}

/** Format time as hh.mm (e.g. 10:41 AM -> "10.41") */
function formatReachedTime(dateStr) {
    if (!dateStr) return "";
    return moment(dateStr).format("hh.mm");
}

/** Format date as DD-MM-YYYY */
function formatDaywise(dateStr) {
    if (!dateStr) return "";
    return moment(dateStr).format("DD-MM-YYYY");
}

export default function Reached_time_report() {
    const { http, user } = AuthUser();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedYear, setSelectedYear] = useState("");
    const [assignedTo, setAssignedTo] = useState("");
    const [users, setUsers] = useState([]);

    useEffect(() => {
        http.get(`/department_user_company?login_user=${user.id}`).then((res) => {
            setUsers(res.data.users || []);
        });
    }, [user.id]);

    useEffect(() => {
        fetchReport(startDate, endDate, assignedTo);
    }, [startDate, endDate, assignedTo]);

    const fetchReport = (start = "", end = "", assigneeId = "") => {
        setIsLoading(true);
        const endpoint = `/get_reached_time_report?login_user=${user.id}&startdate=${start}&enddate=${end}&assigned_to=${assigneeId}`;

        http.get(endpoint)
            .then((res) => {
                setIsLoading(false);
                if (Array.isArray(res.data.data)) {
                    setData(res.data.data);
                } else {
                    setData([]);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching Reached Time report:", error);
                setData([]);
            });
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
        setSelectedMonth("");
        setSelectedYear("");
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
        setSelectedMonth("");
        setSelectedYear("");
    };

    const handleMonthChange = (event) => {
        const month = event.target.value ? parseInt(event.target.value, 10) : "";
        setSelectedMonth(event.target.value);
        if (month && selectedYear) {
            const { start, end } = getMonthRange(parseInt(selectedYear, 10), month);
            setStartDate(start);
            setEndDate(end);
        } else if (!month) {
            setStartDate("");
            setEndDate("");
        }
    };

    const handleYearChange = (event) => {
        const year = event.target.value;
        setSelectedYear(year);
        if (year && selectedMonth) {
            const { start, end } = getMonthRange(parseInt(year, 10), parseInt(selectedMonth, 10));
            setStartDate(start);
            setEndDate(end);
        } else if (!year) {
            setStartDate("");
            setEndDate("");
        }
    };

    const handleAssignedToChange = (event) => {
        setAssignedTo(event.target.value);
    };

    const handleClear = () => {
        setStartDate("");
        setEndDate("");
        setSelectedMonth("");
        setSelectedYear("");
        setAssignedTo("");
    };

    // Build pivot: unique dates (sorted), unique people (sorted), map (date, personName) -> time string
    const dates = [...new Set(data.map((r) => r.date))].sort();
    const people = [...new Set(data.map((r) => r.assigned_to_name))].sort((a, b) => (a || "").localeCompare(b || ""));
    const timeMap = {};
    data.forEach((r) => {
        const key = `${r.date}|${r.assigned_to_name}`;
        timeMap[key] = formatReachedTime(r.reached_time);
    });

    const years = getYears();

    return (
        <>
            <div className="container mt-1">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h1 className="mb-0">Reached Time Report</h1>
                  <button
                    className="btn btn-sm btn-outline-success"
                    disabled={dates.length === 0}
                    onClick={() => downloadCSV(
                      [["DAYWISE", ...people],
                       ...dates.map(date => [
                         formatDaywise(date),
                         ...people.map(name => timeMap[`${date}|${name}`] || "")
                       ])],
                      `reached_time_report_${moment().format("YYYYMMDD_HHmm")}.csv`
                    )}
                  >⬇ CSV</button>
                </div>
                <p className="text-muted">Daywise reached time (OJC) — dates as rows, Assigned To as columns</p>
                <div className="row align-items-end">
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">Assigned To</label>
                        <select
                            className="form-control"
                            value={assignedTo}
                            onChange={handleAssignedToChange}
                        >
                            <option value="">All</option>
                            {users.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {[u.name, u.last_name].filter(Boolean).join(" ")}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">Month</label>
                        <select
                            className="form-control"
                            value={selectedMonth}
                            onChange={handleMonthChange}
                        >
                            <option value="">Select month</option>
                            {MONTHS.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">Year</label>
                        <select
                            className="form-control"
                            value={selectedYear}
                            onChange={handleYearChange}
                        >
                            <option value="">Select year</option>
                            {years.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">From Date</label>
                        <input
                            name="start_date"
                            onChange={handleStartDateChange}
                            className="form-control"
                            type="date"
                            value={startDate}
                        />
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">To Date</label>
                        <input
                            name="end_date"
                            onChange={handleEndDateChange}
                            className="form-control"
                            type="date"
                            value={endDate}
                        />
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2 d-flex align-items-end">
                        <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
                            Clear
                        </button>
                    </div>
                </div>

                <div className="table-responsive">
                    {isLoading ? (
                        <TaskSkeleton />
                    ) : (
                        <table className="table table-bordered table-striped center_tbl reached-time-table">
                            <thead className="table-primary">
                                <tr>
                                    <th className="daywise-col">DAYWISE</th>
                                    {people.map((name) => (
                                        <th key={name} className="text-uppercase">{name || "—"}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {dates.length === 0 && !isLoading ? (
                                    <tr>
                                        <td colSpan={people.length + 1} className="text-center text-muted">
                                            No data. Select date range and ensure OJC tasks have reached (start) time.
                                        </td>
                                    </tr>
                                ) : (
                                    dates.map((date) => (
                                        <tr key={date}>
                                            <td className="daywise-col fw-medium">{formatDaywise(date)}</td>
                                            {people.map((name) => (
                                                <td key={`${date}-${name}`} className="text-center">
                                                    {timeMap[`${date}|${name}`] || ""}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </>
    );
}
